import React from 'react';
import { useTheme, type Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';
import type { SpotlightRect } from './tourGeometry';
import { computeArrowPath, rectsOverlap } from './tourArrowGeometry';

export interface TourArrowLayerProps {
  /** The callout card rectangle. */
  calloutRect: SpotlightRect;
  /** The primary spotlight target rectangle. */
  primaryRect: SpotlightRect | null;
  /** Optional related target rectangles with labels. */
  relatedRects?: readonly { label: string; rect: SpotlightRect }[];
  /** When true, no arrows are rendered even if rects are available. */
  hidden?: boolean;
  /** Theme override; defaults to `useTheme()`. */
  theme?: Theme;
}

interface ArrowItem {
  id: string;
  path: string;
  dashed?: boolean;
  color: string;
}

function buildArrowItems(
  calloutRect: SpotlightRect,
  primaryRect: SpotlightRect | null,
  relatedRects: readonly { label: string; rect: SpotlightRect }[],
  primaryColor: string,
  relatedColor: string
): ArrowItem[] {
  if (primaryRect && !rectsOverlap(calloutRect, primaryRect)) {
    return [
      {
        id: 'primary',
        path: computeArrowPath(calloutRect, primaryRect).path,
        color: primaryColor,
      },
      ...relatedRects
        .filter(({ rect }) => !rectsOverlap(calloutRect, rect))
        .map(({ label, rect }) => ({
          id: `related-${label}`,
          path: computeArrowPath(calloutRect, rect).path,
          dashed: true,
          color: relatedColor,
        })),
    ];
  }

  // When the primary target overlaps the callout, still point out related
  // controls if they are far enough away to be useful.
  return relatedRects
    .filter(({ rect }) => !rectsOverlap(calloutRect, rect))
    .map(({ label, rect }) => ({
      id: `related-${label}`,
      path: computeArrowPath(calloutRect, rect).path,
      dashed: true,
      color: relatedColor,
    }));
}

const TourArrowLayer: React.FC<TourArrowLayerProps> = ({
  calloutRect,
  primaryRect,
  relatedRects = [],
  hidden = false,
  theme: themeProp,
}) => {
  const themeFromHook = useTheme();
  const theme = themeProp ?? themeFromHook;

  if (hidden) {
    return null;
  }

  const items = buildArrowItems(
    calloutRect,
    primaryRect,
    relatedRects,
    alpha(theme.palette.primary.light, 0.92),
    alpha(theme.palette.secondary.light, 0.88)
  );

  if (items.length === 0) {
    return null;
  }

  const viewportWidth = window.innerWidth || 1280;
  const viewportHeight = window.innerHeight || 800;
  const markerId = 'tour-arrow-head';

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      width={viewportWidth}
      height={viewportHeight}
      viewBox={`0 0 ${viewportWidth} ${viewportHeight}`}
      data-testid="workspace-spotlight-arrow-layer"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: theme.zIndex.tooltip + 2,
      }}
    >
      <defs>
        <marker
          id={markerId}
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M0,0 L0,6 L9,3 z" fill={alpha(theme.palette.primary.light, 0.92)} />
        </marker>
      </defs>
      {items.map((item) => (
        <path
          key={item.id}
          d={item.path}
          fill="none"
          stroke={item.color}
          strokeWidth={2}
          strokeDasharray={item.dashed ? '4 4' : undefined}
          markerEnd={item.dashed ? undefined : `url(#${markerId})`}
          data-testid={item.id === 'primary' ? 'workspace-spotlight-primary-arrow' : 'workspace-spotlight-related-arrow'}
          data-arrow-target={item.id}
        />
      ))}
    </svg>
  );
};

export default TourArrowLayer;
